import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, X, CalendarDays, ChevronLeft, ChevronRight, Euro } from "lucide-react";
import { API } from "@/lib/api";

export function CalendarView() {
  const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/events`);
      setEvents(r.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pad = (n) => String(n).padStart(2, "0");
  const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
  const todayKey = dateKey(now.getFullYear(), now.getMonth(), now.getDate());
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  const shiftMonth = (delta) => setCursor((c) => {
    const d = new Date(c.y, c.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const eventsByDay = {};
  events.forEach((e) => {
    const k = (e.start || "").slice(0, 10);
    if (k) (eventsByDay[k] = eventsByDay[k] || []).push(e);
  });

  const startOffset = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const openNew = (key) => setForm({ title: "", date: key || todayKey, time: "", endTime: "", all_day: false, description: "", customer_name: "", color: "#B8924A" });
  const openEdit = (e) => {
    if (e.type === "invoice_due" || e.readonly) {
      toast.info(e.description || e.title, { description: e.overdue ? "Diese Rechnung ist überfällig." : "Rechnungs-Fälligkeit" });
      return;
    }
    setForm({
      id: e.id, title: e.title || "", date: (e.start || "").slice(0, 10),
      time: e.all_day ? "" : (e.start || "").slice(11, 16),
      endTime: e.all_day ? "" : (e.end || "").slice(11, 16),
      all_day: !!e.all_day, description: e.description || "", customer_name: e.customer_name || "", color: e.color || "#B8924A",
    });
  };

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Titel und Datum erforderlich"); return; }
    setSaving(true);
    const start = form.all_day || !form.time ? form.date : `${form.date}T${form.time}`;
    const end = form.all_day ? "" : (form.endTime ? `${form.date}T${form.endTime}` : "");
    const body = { title: form.title, description: form.description, start, end, all_day: form.all_day, color: form.color, customer_name: form.customer_name };
    try {
      if (form.id) await axios.put(`${API}/calendar/events/${form.id}`, body);
      else await axios.post(`${API}/calendar/events`, body);
      toast.success("Termin gespeichert");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!form?.id) return;
    if (!window.confirm("Diesen Termin löschen?")) return;
    try {
      await axios.delete(`${API}/calendar/events/${form.id}`);
      toast.success("Termin gelöscht");
      setForm(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const upcoming = [...events]
    .filter((e) => (e.start || "").slice(0, 10) >= todayKey)
    .sort((a, b) => (a.start || "").localeCompare(b.start || ""))
    .slice(0, 8);
  const fmtUpcoming = (e) => {
    const d = new Date((e.start || "").length <= 10 ? `${e.start}T00:00` : e.start);
    const day = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short" });
    return e.all_day ? `${day} · ganztägig` : `${day} · ${(e.start || "").slice(11, 16)} Uhr`;
  };

  return (
    <div data-testid="calendar-view" className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <Card className="p-5 border-[var(--brand-border)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg hover:bg-[var(--brand-border-light)]" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
            <div className="text-lg font-semibold text-[var(--brand-ink)] min-w-[170px] text-center capitalize" data-testid="cal-month-label">{monthLabel}</div>
            <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg hover:bg-[var(--brand-border-light)]" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
            <button onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })} className="ml-1 text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline" data-testid="cal-today">Heute</button>
          </div>
          <Button onClick={() => openNew(todayKey)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="cal-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Neuer Termin
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-semibold text-[var(--brand-muted)] py-1">{w}</div>)}
        </div>
        {events.some((e) => e.type === "invoice_due") && (
          <div className="flex items-center gap-4 mb-2 text-xs text-[var(--brand-muted)]" data-testid="cal-legend">
            <span className="flex items-center gap-1"><Euro className="w-3 h-3" style={{ color: "#D97706" }} /> Rechnung fällig</span>
            <span className="flex items-center gap-1"><Euro className="w-3 h-3" style={{ color: "#DC2626" }} /> Überfällig</span>
          </div>
        )}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[86px] rounded-lg bg-transparent" />;
            const key = dateKey(cursor.y, cursor.m, d);
            const dayEvents = eventsByDay[key] || [];
            const isToday = key === todayKey;
            return (
              <div key={i} onClick={() => openNew(key)} data-testid={`cal-day-${key}`}
                className={`min-h-[86px] rounded-lg border p-1.5 cursor-pointer transition hover:border-[var(--brand-primary)] ${isToday ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]" : "border-[var(--brand-border-light)]"}`}>
                <div className={`text-xs font-medium mb-1 ${isToday ? "text-[var(--brand-primary)]" : "text-[var(--brand-ink)]"}`}>{d}</div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((e) => (
                    <div key={e.id} onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                      className="text-[11px] leading-tight truncate rounded px-1.5 py-0.5 text-white flex items-center gap-1" style={{ background: e.color || "#B8924A" }} title={e.description || e.title} data-testid={e.type === "invoice_due" ? `cal-invoice-${e.quote_id}` : undefined}>
                      {e.type === "invoice_due" ? <Euro className="w-2.5 h-2.5 shrink-0" /> : null}
                      <span className="truncate">{!e.all_day && (e.start || "").slice(11, 16) ? `${(e.start || "").slice(11, 16)} ` : ""}{e.title}</span>
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-[var(--brand-muted)] pl-1">+{dayEvents.length - 3} mehr</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 border-[var(--brand-border)] h-fit">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-5 h-5 text-[var(--brand-primary)]" />
          <span className="font-semibold text-[var(--brand-ink)]">Anstehende Termine</span>
        </div>
        {loading ? (
          <p className="text-sm text-[var(--brand-muted)]">Lädt …</p>
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-[var(--brand-muted)]" data-testid="cal-upcoming-empty">Keine anstehenden Termine.</p>
        ) : (
          <div className="space-y-2" data-testid="cal-upcoming">
            {upcoming.map((e) => (
              <button key={e.id} onClick={() => openEdit(e)} className="w-full text-left rounded-lg border border-[var(--brand-border-light)] p-2.5 hover:border-[var(--brand-primary)] transition">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: e.color || "#B8924A" }} />
                  {e.type === "invoice_due" && <Euro className="w-3 h-3 shrink-0" style={{ color: e.color }} />}
                  <span className="text-sm font-medium text-[var(--brand-ink)] truncate">{e.title}</span>
                </div>
                <div className="text-xs text-[var(--brand-muted)] mt-0.5">{fmtUpcoming(e)}{e.customer_name ? ` · ${e.customer_name}` : ""}</div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="cal-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--brand-ink)]">{form.id ? "Termin bearbeiten" : "Neuer Termin"}</h3>
              <button onClick={() => setForm(null)} className="p-1" data-testid="cal-modal-close"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Titel</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Kundentermin" data-testid="cal-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Datum</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cal-date" />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm text-[var(--brand-ink)] cursor-pointer">
                    <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} data-testid="cal-allday" />
                    Ganztägig
                  </label>
                </div>
              </div>
              {!form.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Von</Label>
                    <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="cal-time" />
                  </div>
                  <div>
                    <Label>Bis</Label>
                    <Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} data-testid="cal-endtime" />
                  </div>
                </div>
              )}
              <div>
                <Label>Kunde (optional)</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Name des Kunden" data-testid="cal-customer" />
              </div>
              <div>
                <Label>Notiz (optional)</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cal-desc" />
              </div>
              <div>
                <Label>Farbe</Label>
                <div className="flex gap-2 mt-1">
                  {["#B8924A", "#2563EB", "#059669", "#DC2626", "#7C3AED", "#D97706"].map((c) => (
                    <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-[var(--brand-ink)]" : "border-transparent"}`} style={{ background: c }} data-testid={`cal-color-${c}`} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mt-5">
              {form.id ? (
                <Button variant="outline" onClick={remove} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="cal-delete"><Trash2 className="w-4 h-4 mr-1" /> Löschen</Button>
              ) : <span />}
              <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="cal-save">{saving ? "Speichert …" : "Speichern"}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
